import { useTranslation } from 'react-i18next';
import { useSetAtom } from 'jotai';

import type { DropdownOption } from '@/ui/dropdown/dropdown';
import { WithDropdown } from '@/ui/dropdown/dropdown';
import { MenuButton } from '@/ui/menu-button/menu-button';
import { PERMISSION } from '@/user/permissions/permission.type';
import { useUserPermissions } from '@/user/permissions/use-user-permissions';
import type { ContentItem } from '@/api/v4/content-items.api';
import { modalState } from '@/state/modal.state';
import { useToggleContentItemSidebar } from './sidebar/use-toggle-content-item-sidebar';
import { useDeleteContentItem } from './use-delete-content-item.mutation';

export const ContentItemActionsMenu = ({
  contentItem,
}: {
  contentItem: ContentItem;
}) => {
  const { t } = useTranslation('settings', { keyPrefix: 'feedTab' });
  const { hasRequiredPermission } = useUserPermissions();
  const setModal = useSetAtom(modalState);

  const { navigateToContentItemSidebar } = useToggleContentItemSidebar();
  const { deleteContentItem } = useDeleteContentItem();

  const canManageFeed = hasRequiredPermission(PERMISSION.MANAGE_FEED);

  const menu: DropdownOption[] = [
    {
      label: t`edit`,
      isVisible: canManageFeed,
      onClick: () => {
        navigateToContentItemSidebar(contentItem);
      },
    },
    {
      label: t`delete.menuItem`,
      isVisible: canManageFeed,
      onClick: () => {
        setModal({
          state: 'confirmDelete',
          title: t`delete.title`,
          description: t`delete.description`,
          confirmLabel: t`delete.confirmButton`,
          onConfirm: () => {
            void deleteContentItem(contentItem.id);
          },
        });
      },
    },
  ];

  return (
    <WithDropdown
      items={menu}
      options={{
        placement: 'bottom-start',
        modifiers: [{ name: 'offset', options: { offset: [0, 5.5] } }],
      }}
    >
      <MenuButton />
    </WithDropdown>
  );
};
